/**
 * Privacy Controls Component
 * Allows patients to manage their data privacy settings, export data, and control data sharing
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Checkbox,
  Slider
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  DataUsage as DataUsageIcon,
  Storage as StorageIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// API Services
import { privacyApi } from '../../services/api/patientManagement';

const PrivacyControls = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [privacySettings, setPrivacySettings] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAccessLogDialog, setShowAccessLogDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  
  // Form states
  const [privacyForm, setPrivacyForm] = useState({
    data_sharing_enabled: false,
    profile_visibility: 'private',
    search_visibility: false,
    data_retention_years: 7,
    auto_delete_enabled: false,
    anonymize_data: false,
    marketing_communications: false,
    research_participation: false,
    emergency_contact_sharing: false,
    healthcare_provider_access: true,
    third_party_sharing: false,
    data_portability: true,
    right_to_rectification: true,
    right_to_erasure: true
  });

  const [exportForm, setExportForm] = useState({
    include_personal_data: true,
    include_medical_data: true,
    include_communication_data: true,
    include_billing_data: true,
    include_consent_data: true,
    format: 'json',
    date_range: 'all'
  });

  const [deleteForm, setDeleteForm] = useState({
    confirm_deletion: false,
    reason: '',
    backup_data: false
  });

  const visibilityOptions = [
    { value: 'private', label: 'Private', description: 'Only you can see your profile', icon: <LockIcon /> },
    { value: 'healthcare_providers', label: 'Healthcare Providers', description: 'Visible to your healthcare team', icon: <HospitalIcon /> },
    { value: 'public', label: 'Public', description: 'Visible to everyone', icon: <PublicIcon /> }
  ];

  const retentionOptions = [
    { value: 1, label: '1 Year' },
    { value: 3, label: '3 Years' },
    { value: 5, label: '5 Years' },
    { value: 7, label: '7 Years' },
    { value: 10, label: '10 Years' },
    { value: 0, label: 'Indefinitely' }
  ];

  useEffect(() => {
    loadPrivacySettings();
    loadAccessLogs();
  }, []);

  const loadPrivacySettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await privacyApi.getPrivacySettings();
      setPrivacySettings(response);
      setPrivacyForm(response);
    } catch (err) {
      console.error('Error loading privacy settings:', err);
      setError('Failed to load privacy settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAccessLogs = async () => {
    try {
      const response = await privacyApi.getAccessLogs();
      setAccessLogs(response);
    } catch (err) {
      console.error('Error loading access logs:', err);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      setLoading(true);
      await privacyApi.updatePrivacySettings(privacyForm);
      toast.success('Privacy settings updated successfully!');
      setShowSettingsDialog(false);
      await loadPrivacySettings();
    } catch (err) {
      console.error('Error updating privacy settings:', err);
      toast.error('Failed to update privacy settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDataExport = async () => {
    try {
      setLoading(true);
      const exportData = await privacyApi.exportData();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patient-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully!');
      setShowExportDialog(false);
    } catch (err) {
      console.error('Error exporting data:', err);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDataDeletion = async () => {
    try {
      setLoading(true);
      await privacyApi.deleteData();
      toast.success('Data deletion request submitted successfully!');
      setShowDeleteDialog(false);
      await loadPrivacySettings();
    } catch (err) {
      console.error('Error deleting data:', err);
      toast.error('Failed to delete data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getVisibilityIcon = (visibility) => {
    const option = visibilityOptions.find(opt => opt.value === visibility);
    return option ? option.icon : <LockIcon />;
  };

  const getVisibilityLabel = (visibility) => {
    const option = visibilityOptions.find(opt => opt.value === visibility);
    return option ? option.label : visibility;
  };

  const getVisibilityDescription = (visibility) => {
    const option = visibilityOptions.find(opt => opt.value === visibility);
    return option ? option.description : '';
  };

  const StatCard = ({ title, value, icon, color, onClick, description }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            {description && (
              <Typography variant="caption" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const PrivacySettingCard = ({ title, description, icon, children, color = 'primary.main' }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  if (loading && !privacySettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Privacy Controls
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setShowSettingsDialog(true)}
        >
          Update Settings
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Data Sharing"
            value={privacySettings?.data_sharing_enabled ? 'Enabled' : 'Disabled'}
            icon={<ShareIcon />}
            color={privacySettings?.data_sharing_enabled ? 'success.main' : 'error.main'}
            description="Controls data sharing with healthcare providers"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Profile Visibility"
            value={getVisibilityLabel(privacySettings?.profile_visibility)}
            icon={getVisibilityIcon(privacySettings?.profile_visibility)}
            color="primary.main"
            description="Who can see your profile"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Data Retention"
            value={privacySettings?.data_retention_years ? `${privacySettings.data_retention_years} years` : 'Indefinite'}
            icon={<StorageIcon />}
            color="info.main"
            description="How long your data is kept"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Access Logs"
            value={accessLogs.length}
            icon={<HistoryIcon />}
            color="secondary.main"
            description="Recent data access events"
            onClick={() => setShowAccessLogDialog(true)}
          />
        </Grid>
      </Grid>

      {/* Privacy Settings */}
      <Grid container spacing={3}>
        {/* Data Sharing Controls */}
        <Grid item xs={12} md={6}>
          <PrivacySettingCard
            title="Data Sharing Controls"
            description="Control how your data is shared with healthcare providers"
            icon={<ShareIcon />}
            color="primary.main"
          >
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.data_sharing_enabled || false}
                    disabled
                  />
                }
                label="Enable data sharing with healthcare providers"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.healthcare_provider_access || false}
                    disabled
                  />
                }
                label="Allow healthcare providers to access your data"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.third_party_sharing || false}
                    disabled
                  />
                }
                label="Allow third-party data sharing"
              />
            </Stack>
          </PrivacySettingCard>
        </Grid>

        {/* Profile Visibility */}
        <Grid item xs={12} md={6}>
          <PrivacySettingCard
            title="Profile Visibility"
            description="Control who can see your profile information"
            icon={<VisibilityIcon />}
            color="secondary.main"
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current visibility: {getVisibilityLabel(privacySettings?.profile_visibility)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getVisibilityDescription(privacySettings?.profile_visibility)}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.search_visibility || false}
                    disabled
                  />
                }
                label="Allow profile to be found in search"
              />
            </Stack>
          </PrivacySettingCard>
        </Grid>

        {/* Data Retention */}
        <Grid item xs={12} md={6}>
          <PrivacySettingCard
            title="Data Retention"
            description="Control how long your data is kept"
            icon={<StorageIcon />}
            color="info.main"
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Retention period: {privacySettings?.data_retention_years ? `${privacySettings.data_retention_years} years` : 'Indefinite'}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.auto_delete_enabled || false}
                    disabled
                  />
                }
                label="Enable automatic data deletion"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.anonymize_data || false}
                    disabled
                  />
                }
                label="Anonymize data when possible"
              />
            </Stack>
          </PrivacySettingCard>
        </Grid>

        {/* Communication Preferences */}
        <Grid item xs={12} md={6}>
          <PrivacySettingCard
            title="Communication Preferences"
            description="Control how you receive communications"
            icon={<EmailIcon />}
            color="success.main"
          >
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.marketing_communications || false}
                    disabled
                  />
                }
                label="Receive marketing communications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.research_participation || false}
                    disabled
                  />
                }
                label="Participate in research studies"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacySettings?.emergency_contact_sharing || false}
                    disabled
                  />
                }
                label="Share emergency contact information"
              />
            </Stack>
          </PrivacySettingCard>
        </Grid>

        {/* Data Rights */}
        <Grid item xs={12}>
          <PrivacySettingCard
            title="Data Rights"
            description="Your rights regarding your personal data"
            icon={<GavelIcon />}
            color="warning.main"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={privacySettings?.data_portability || false}
                      disabled
                    />
                  }
                  label="Data Portability"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={privacySettings?.right_to_rectification || false}
                      disabled
                    />
                  }
                  label="Right to Rectification"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={privacySettings?.right_to_erasure || false}
                      disabled
                    />
                  }
                  label="Right to Erasure"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setShowExportDialog(true)}
                  fullWidth
                >
                  Export Data
                </Button>
              </Grid>
            </Grid>
          </PrivacySettingCard>
        </Grid>
      </Grid>

      {/* Data Actions */}
      <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Data Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setShowExportDialog(true)}
              fullWidth
            >
              Export Your Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setShowAccessLogDialog(true)}
              fullWidth
            >
              View Access Logs
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteDialog(true)}
              fullWidth
            >
              Delete All Data
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onClose={() => setShowSettingsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update Privacy Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Data Sharing Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.data_sharing_enabled}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, data_sharing_enabled: e.target.checked })}
                  />
                }
                label="Enable data sharing with healthcare providers"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.healthcare_provider_access}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, healthcare_provider_access: e.target.checked })}
                  />
                }
                label="Allow healthcare providers to access your data"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.third_party_sharing}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, third_party_sharing: e.target.checked })}
                  />
                }
                label="Allow third-party data sharing"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Profile Visibility
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Profile Visibility</InputLabel>
                <Select
                  value={privacyForm.profile_visibility}
                  onChange={(e) => setPrivacyForm({ ...privacyForm, profile_visibility: e.target.value })}
                  label="Profile Visibility"
                >
                  {visibilityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {option.icon}
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="body1">{option.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.search_visibility}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, search_visibility: e.target.checked })}
                  />
                }
                label="Allow profile to be found in search"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Data Retention
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Data Retention Period</InputLabel>
                <Select
                  value={privacyForm.data_retention_years}
                  onChange={(e) => setPrivacyForm({ ...privacyForm, data_retention_years: e.target.value })}
                  label="Data Retention Period"
                >
                  {retentionOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.auto_delete_enabled}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, auto_delete_enabled: e.target.checked })}
                  />
                }
                label="Enable automatic data deletion"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.anonymize_data}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, anonymize_data: e.target.checked })}
                  />
                }
                label="Anonymize data when possible"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Communication Preferences
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.marketing_communications}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, marketing_communications: e.target.checked })}
                  />
                }
                label="Receive marketing communications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.research_participation}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, research_participation: e.target.checked })}
                  />
                }
                label="Participate in research studies"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={privacyForm.emergency_contact_sharing}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, emergency_contact_sharing: e.target.checked })}
                  />
                }
                label="Share emergency contact information"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateSettings} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Update Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will export all your patient data. Select what to include:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportForm.include_personal_data}
                    onChange={(e) => setExportForm({ ...exportForm, include_personal_data: e.target.checked })}
                  />
                }
                label="Personal Information (Profile, Contact Details)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportForm.include_medical_data}
                    onChange={(e) => setExportForm({ ...exportForm, include_medical_data: e.target.checked })}
                  />
                }
                label="Medical Data (History, Records, Prescriptions)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportForm.include_communication_data}
                    onChange={(e) => setExportForm({ ...exportForm, include_communication_data: e.target.checked })}
                  />
                }
                label="Communication (Messages, Appointments)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportForm.include_billing_data}
                    onChange={(e) => setExportForm({ ...exportForm, include_billing_data: e.target.checked })}
                  />
                }
                label="Billing Data (Invoices, Payments)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportForm.include_consent_data}
                    onChange={(e) => setExportForm({ ...exportForm, include_consent_data: e.target.checked })}
                  />
                }
                label="Consent Data (Privacy Settings, Permissions)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button onClick={handleDataExport} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Export Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Logs Dialog */}
      <Dialog open={showAccessLogDialog} onClose={() => setShowAccessLogDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Data Access Logs</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Recent access to your data by healthcare providers and system administrators.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Accessor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Data Type</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((log, index) => (
                  <TableRow key={index}>
                    <TableCell>{format(new Date(log.timestamp), 'MMM d, yyyy HH:mm')}</TableCell>
                    <TableCell>{log.accessor}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.data_type}</TableCell>
                    <TableCell>{log.ip_address}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={accessLogs.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAccessLogDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Data Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete All Data</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Warning:</strong> This action cannot be undone. All your patient data will be permanently deleted.
            </Typography>
          </Alert>
          <Typography variant="body1" gutterBottom>
            This will delete:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><PersonIcon /></ListItemIcon>
              <ListItemText primary="Profile Information" />
            </ListItem>
            <ListItem>
              <ListItemIcon><HistoryIcon /></ListItemIcon>
              <ListItemText primary="Medical History" />
            </ListItem>
            <ListItem>
              <ListItemIcon><DocumentIcon /></ListItemIcon>
              <ListItemText primary="All Documents" />
            </ListItem>
            <ListItem>
              <ListItemIcon><EmailIcon /></ListItemIcon>
              <ListItemText primary="Communication History" />
            </ListItem>
          </List>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteForm.confirm_deletion}
                onChange={(e) => setDeleteForm({ ...deleteForm, confirm_deletion: e.target.checked })}
              />
            }
            label="I understand that this action cannot be undone"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDataDeletion} 
            color="error" 
            variant="contained" 
            disabled={loading || !deleteForm.confirm_deletion}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete All Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrivacyControls;
